import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { useCreateTicket } from '../hooks';
import { ARCHE_PATH } from '../constants';

/**
 * Standalone manual ticket-creation form. Reachable at
 * /a/arche_customer_support/tickets/new. Used as a fallback path when the
 * AI-driven chat flow can't be used (for example, when the user is out of
 * credits and the support chat would otherwise refuse to respond).
 */
export function NewTicketPage() {
  const navigate = useNavigate();
  const { createTicket, loading, error } = useCreateTicket();
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');

  const trimmedSubject = subject.trim();
  const trimmedDescription = description.trim();
  const canSubmit = trimmedSubject.length > 0 && trimmedDescription.length > 0 && !loading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    try {
      const created = await createTicket({
        subject: trimmedSubject,
        description: trimmedDescription,
      });
      navigate(`${ARCHE_PATH}/tickets/${created.ticketId}`);
    } catch {
      // Error surfaced via the hook's `error` field below.
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6">
      <Link to={`${ARCHE_PATH}/tickets`}>
        <Button variant="ghost" size="sm">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to My Tickets
        </Button>
      </Link>

      <div className="space-y-1">
        <h1 className="text-2xl font-bold">New support ticket</h1>
        <p className="text-sm text-muted-foreground">
          Describe your issue and our support team will respond.
        </p>
      </div>

      <Card>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="ticket-subject" className="text-sm font-medium">
                Subject
              </label>
              <input
                id="ticket-subject"
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Brief summary of your issue"
                maxLength={200}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                disabled={loading}
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="ticket-description" className="text-sm font-medium">
                Description
              </label>
              <Textarea
                id="ticket-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What happened? What did you expect? Include any steps to reproduce."
                rows={8}
                maxLength={5000}
                disabled={loading}
              />
            </div>

            {error && (
              <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <Button type="submit" disabled={!canSubmit}>
                {loading ? 'Submitting…' : 'Submit ticket'}
              </Button>
              <Link to={`${ARCHE_PATH}/tickets`}>
                <Button type="button" variant="outline">
                  Cancel
                </Button>
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
